<script setup lang="ts">
import {
  CreateOrUpdateDialog,
  DeleteDialog,
  ProjectIndex,
  WorkOrderField
} from '@haskoning/vue-component-library'
import { onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/project'
import { useAuthorizationStore } from '@/stores/authorization'
import router from '@/router'
import type { ProjectType } from '@/stores/type/project.type'
import { useAuthenticationStore } from '@/stores/authentication'
import { useProjectUserStore } from '@/stores/projectUser'

const authorizationStore = useAuthorizationStore()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authStore = useAuthenticationStore()

const { user } = storeToRefs(authStore)
const {
  items,
  currentItem,
  createUpdateDialog,
  deleteDialog,
  deleteItem,
  writeLoading: loading,
  deleteLoading,
  fieldsFiltered,
  fieldsList
} = storeToRefs(projectStore)

const addProject = () => {
  currentItem.value = {} as ProjectType
  createUpdateDialog.value = true
}

const createOrUpdateProject = async (item: ProjectType) => {
  await projectStore.postOrUpdate(item)
  await createUpdateDialogClosed()
}

const createUpdateDialogClosed = async () => {
  currentItem.value = null
  await projectStore.servicePermissionItems()
}

const deleteProject = (item: ProjectType) => {
  deleteItem.value = item
  deleteDialog.value = true
}

const editItem = (item: ProjectType) => {
  currentItem.value = item
  createUpdateDialog.value = true
}

const navigateToProject = (projectID: number) => {
  router.push({ name: 'project-detail', params: { id: projectID } })
}

const codeField = computed(() => {
  return fieldsList.value.find((field: any) => field.parameter === 'work_order')
})

onMounted(async () => {
  await authorizationStore.fetchItems()
  if (user.value) {
    authorizationStore.setCurrentRole([user.value.role])
  }
  if (!items || !items.value.length) {
    await projectStore.fetchOptions()
    await projectStore.fetchItems()
  }
  await projectStore.servicePermissionItems()
})

onUnmounted(() => {
  projectUserStore.resetState()
  authorizationStore.resetState()
})
</script>

<template>
  <div class="fill-height">
    <ProjectIndex
      title="Liquefaction"
      :items="items"
      :loading="loading"
      :get-change-permission="projectStore.getChangePermission"
      :get-delete-permission="projectStore.getDeletePermission"
      @navigate-to-project="navigateToProject"
      @add-item="addProject"
      @edit-item="editItem"
      @delete-item="deleteProject"
    />
    <CreateOrUpdateDialog
      v-if="currentItem"
      v-model="createUpdateDialog"
      :loading="loading"
      v-model:item="currentItem"
      name="project"
      :fields="fieldsFiltered"
      @submit="createOrUpdateProject"
    >
      <WorkOrderField
        v-if="codeField"
        :field="codeField"
        validate-code
        v-model="currentItem"
      ></WorkOrderField>
    </CreateOrUpdateDialog>
    <DeleteDialog
      v-if="deleteItem"
      v-model="deleteDialog"
      :loading="deleteLoading"
      :name="deleteItem?.name"
      :item="deleteItem"
      @set-delete-item="deleteItem = $event"
      @delete="projectStore.delete(deleteItem)"
    ></DeleteDialog>
  </div>
</template>

<style scoped>
:deep(.v-table__wrapper table) {
  border: 1px solid lightgrey;
}
</style>
